import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { msalConfig } from 'config/msalConfig';
import {
	getDoctorTypes,
	getReasonCodeCanceled,
	getReasonCodePending,
	getReasonCodeReject,
	getReasonCodeReturn,
	getReasonCodeVoid,
} from 'feature/claims/claimSlice';
import {
	getClaimsNotifications,
	getClaimsNotificationsStatuses,
} from 'feature/claims/claimsSlice';
import { getClients } from 'feature/clients/clientsSlice';
import { getCookiesContent } from 'feature/cookies/cookiesSlice';
import { getDocuments } from 'feature/documents/documentsSlice';
import { getFooterLinks } from 'feature/footer/footerSlice';
import {
	getClaimsStatuses,
	getComplaintOutcomes,
	getComplaintStatuses,
	getComplaintTypes,
	getFraudStatuses,
	getReturnStatuses,
} from 'feature/form/formSlice';
import { getLocationInfo } from 'feature/locationInformation/locationInformationSlice';
import { getProfile, getRoles, logout } from 'feature/user/userSlice';
import { healthCheck } from 'feature/utils/checkConnectionSlice';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { cleanupStorage } from 'utils/localStorage';
import { useSse } from 'utils/sse.hook';
import { getLocaleLanguage } from 'utils/utils';
import useDialogLocation, { FIRST_LOCATION } from './DialogLocation.hook';

const findRightAccount = (accounts: AccountInfo[]) =>
	accounts.find(el => el.idTokenClaims?.aud === msalConfig.auth.clientId);

export const useLogin = () => {
	const { instance, accounts, inProgress } = useMsal();
	const account = useAccount();

	if (!account) {
		const rightAccount = findRightAccount(accounts);
		rightAccount && instance.setActiveAccount(rightAccount);
	}

	useEffect(() => {
		if (inProgress === InteractionStatus.None && !findRightAccount(accounts)) {
			const tokenRequest = {
				scopes: [],
			};
			cleanupStorage();
			instance.acquireTokenRedirect(tokenRequest);
		}
	}, [accounts, inProgress, instance]);
};

export const useLogout = () => {
	const { instance, accounts } = useMsal();
	const dispatch = useDispatch<AppDispatch>();

	return useCallback(() => {
		dispatch(logout({ instance, accounts }));
	}, [accounts, dispatch, instance]);
};

export const useCheckAlive = () => {
	const isAuthenticated = useIsAuthenticated();
	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		if (isAuthenticated) {
			dispatch(healthCheck(''));
		}
	}, [dispatch, isAuthenticated]);
};

export const useGetLocales = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { alive, tokenExpired } = useSelector(
		(state: RootState) => state.healthCheck,
	);

	const handleLogout = useLogout();

	useEffect(() => {
		if (alive) {
			dispatch(getClaimsStatuses(''));
		}
	}, [dispatch, alive]);

	useEffect(() => {
		if (tokenExpired) {
			handleLogout();
		}
	}, [dispatch, handleLogout, tokenExpired]);
};

export const useGetRoles = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { profile } = useSelector((store: RootState) => store.user);

	useEffect(() => {
		if (profile) {
			dispatch(getRoles(''));
		}
	}, [dispatch, profile]);
};

export const useCookies = () => {
	const dispatch = useDispatch<AppDispatch>();

	const { alive } = useSelector((state: RootState) => state.healthCheck);
	const { cookiesConfirmed } = useSelector((state: RootState) => state.user);

	useEffect(() => {
		if (alive && !cookiesConfirmed) {
			//cookies could be confirmed from local storage
			dispatch(getCookiesContent(''));
		}
	}, [dispatch, cookiesConfirmed, alive]);
};

export const useGetProfile = () => {
	const dispatch = useDispatch<AppDispatch>();
	const dialogLocation = useDialogLocation();
	const lang = getLocaleLanguage();

	const { profile, cookiesConfirmed } = useSelector(
		(store: RootState) => store.user,
	);
	const { alive } = useSelector((state: RootState) => state.healthCheck);

	useEffect(() => {
		if (alive && cookiesConfirmed) {
			dispatch(getProfile(''));
		}
	}, [dispatch, cookiesConfirmed, alive]);

	useEffect(() => {
		if (cookiesConfirmed && profile && !profile.location) {
			dialogLocation.open(FIRST_LOCATION);
		}
	}, [profile, dialogLocation, cookiesConfirmed]);

	useEffect(() => {
		if (profile) {
			dispatch(getFooterLinks(''));
		}
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile && lang) dispatch(getDocuments({}));
	}, [dispatch, lang, profile]);

	useEffect(() => {
		if (profile) dispatch(getReasonCodeVoid({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getReasonCodeCanceled({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getReasonCodeReject({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getReasonCodeReturn({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getReturnStatuses({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getReasonCodePending({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getDoctorTypes(''));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getClients({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile) dispatch(getFraudStatuses(''));
	}, [profile, dispatch]);

	useEffect(() => {
		if (profile) {
			dispatch(getComplaintStatuses(''));
			dispatch(getComplaintTypes(''));
			dispatch(getComplaintOutcomes(''));
		}
	}, [profile, dispatch]);

	useEffect(() => {
		if (profile) dispatch(getClaimsNotificationsStatuses({}));
	}, [dispatch, profile]);

	useEffect(() => {
		if (profile && profile.location)
			dispatch(getLocationInfo({ providerId: profile.providerId }));
	}, [dispatch, profile]);

	//start SSE
	useSse(profile?.providerId);
};

export const useGetNotifications = (route: string) => {
	const dispatch = useDispatch<AppDispatch>();
	const { profile } = useSelector((store: RootState) => store.user);

	// get notifications
	useEffect(() => {
		if (profile)
			dispatch(getClaimsNotifications({ limit: '8', showloader: false }));
	}, [dispatch, profile, route]);
};
